import { memo, useContext } from 'react';
import { Breadcrumbs } from 'src/sensors/Breadcrumbs';
import { MESSAGES } from 'src/modules/common/constants';
import { RuntimeContext } from 'src/runtimeContext';
import { PATHS } from 'src/sensors/common/types/Paths';
import { LocationState } from 'src/sensors/LocationState';
import { BREADCRUMB_STATUS } from '../../../../common/types/BreadcrumbStatus';
import { BreadcrumbContainer } from '../../../../common/Breadcrumb/BreadcrumbContainer';

const SENSOR_BREADCRUMB_PAGES = {
    [PATHS.Sensors]: 0,
    [PATHS.Accessories]: 1,
    [PATHS.Checkout]: 2,
};

const getPageStatus = (
    comparing: keyof typeof SENSOR_BREADCRUMB_PAGES,
    comparedTo: keyof typeof SENSOR_BREADCRUMB_PAGES,
) => {
    if (SENSOR_BREADCRUMB_PAGES[comparing] === SENSOR_BREADCRUMB_PAGES[comparedTo]) {
        return BREADCRUMB_STATUS.PROGRESS;
    }

    if (SENSOR_BREADCRUMB_PAGES[comparing] < SENSOR_BREADCRUMB_PAGES[comparedTo]) {
        return BREADCRUMB_STATUS.EMPTY;
    }

    return BREADCRUMB_STATUS.DONE;
};

type Props = {
    currentPage: keyof typeof SENSOR_BREADCRUMB_PAGES;
    locationState: LocationState;
    searchParams: URLSearchParams;
    resetHandler?: () => void;
}

export const SensorBreadcrumbs = memo(({ currentPage, locationState, searchParams, resetHandler }: Props) => {
    const { isMobile } = useContext(RuntimeContext);

    const isAccessoriesDisabled = !locationState.sensorLabel;

    const breadcrumbProps: Breadcrumbs = [
        {
            title: MESSAGES.SelectedSensor,
            value: locationState.sensorLabel || '-',
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Sensors),
            linkState: locationState,
            onActionClick: resetHandler,
            to: {
                pathname: [
                    PATHS.Sensors,
                    locationState.sensorsPage,
                ].join('/'),
                search: searchParams.toString(),
            },
        },
        {
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Accessories),
            title: MESSAGES.SelectedAccessories,
            value: locationState.selectedAccessories.length > 0
                ? locationState.selectedAccessories.map(({ label }) => label).join(', ') : '-',
            disabled: isAccessoriesDisabled,
            linkState: locationState,
            onActionClick: resetHandler,
            to: {
                pathname: [
                    PATHS.Accessories,
                    encodeURIComponent(locationState.sensorLabel),
                    locationState.sensorsPage,
                ].join('/'),
                search: searchParams.toString(),
            },
        },
        {
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Checkout),
            title: MESSAGES.checkoutSensorsBreadcrumbTitle,
            value: '',
            disabled: isAccessoriesDisabled,
            linkState: locationState,
            onActionClick: resetHandler,
            to: {
                pathname: [
                    PATHS.Checkout,
                    encodeURIComponent(locationState.sensorLabel),
                    locationState.sensorsPage,
                ].join('/'),
                search: searchParams.toString(),
            },
        },
    ];

    return (
        <BreadcrumbContainer
            isMobile={isMobile}
            breadcrumbs={breadcrumbProps}
            containerWrapperClass="vss-sensors-breadcrumbs"
        />
    );
});
